import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, ChevronDown, Mail, MapPin, AlertCircle } from 'lucide-react';

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing our website at vibhraad.com or engaging our services, you confirm that you have read, understood, and agree to be bound by these Terms of Service and all applicable laws and regulations.

If you are entering into this agreement on behalf of a business or organisation, you represent that you have the authority to bind that entity to these terms.

**If you do not agree to these terms, please discontinue use of our services immediately.**

These Terms of Service apply to all visitors, users, and clients of Vibhraad Technology Private Limited. We reserve the right to update these terms at any time with notice.`,
  },
  {
    id: 'services',
    title: '2. Services Provided',
    content: `Vibhraad Technology provides the following services ("Services"):

• **Custom Software Development:** Web applications, enterprise systems, APIs, and backend development tailored to business requirements.
• **Mobile App Development:** Native and cross-platform iOS and Android applications.
• **E-commerce Development:** Online stores, payment gateway integration, and inventory management.
• **Digital Marketing:** SEO, PPC campaigns, social media management, and content marketing.
• **ERP / CRM / POS Systems:** Custom business management software including Point of Sale, Customer Relationship Management, and Enterprise Resource Planning systems.
• **UI/UX Design:** User interface design, prototyping, and user experience consulting.
• **Technical Consulting:** Technology strategy, architecture review, and project management advisory.

All services are subject to separate project agreements, statements of work, or service contracts that supplement these Terms.`,
  },
  {
    id: 'payment',
    title: '3. Payment Terms',
    content: `**Milestone-Based Payments:** Projects are typically structured around defined milestones. Payment is due at each milestone as agreed in the project contract.

**Invoicing:** Invoices are issued upon milestone completion and are due within 14 days unless otherwise agreed in writing.

**Late Payments:** Invoices unpaid after 30 days may attract a late payment fee of 1.5% per month. Continued non-payment may result in suspension of services.

**Currency:** All payments are in Indian Rupees (INR) unless otherwise agreed. International clients may pay in USD or GBP subject to a written agreement.

**Advance Payments:** An initial deposit (typically 30-50% of project value) is required before project commencement. This deposit is non-refundable once work has commenced.

**Taxes:** Prices exclude GST and applicable taxes, which will be added to invoices as required by law.`,
  },
  {
    id: 'intellectual',
    title: '4. Intellectual Property',
    content: `**Client Ownership:** Upon full payment of all invoices, all custom code, designs, and deliverables created specifically for you become your property.

**Vibhraad Ownership:** Vibhraad Technology retains ownership of:
• Pre-existing frameworks, libraries, and tools used in your project
• Our proprietary methodologies and processes
• Generic code components and templates

**Open Source:** We may incorporate open-source components under their respective licences. We will disclose any such components upon request.

**Portfolio Rights:** Unless you expressly prohibit it in writing, we reserve the right to display completed work in our portfolio and marketing materials.

**Confidentiality:** We treat all client materials, data, and business information as confidential and will not share them with third parties without consent.`,
  },
  {
    id: 'delivery',
    title: '5. Project Delivery & Timelines',
    content: `**Project Timelines:** Estimated delivery dates are provided in good faith based on the scope agreed at project start. Timelines may change due to:
• Changes in project scope requested by the client
• Delays in client providing required assets, feedback, or approvals
• Third-party dependencies (APIs, hosting, domain setup)
• Force majeure events

**Client Responsibilities:** Timely delivery requires your cooperation. You agree to:
• Provide all required content, assets, and information promptly
• Review and approve deliverables within agreed review windows (typically 5 business days)
• Designate a point of contact authorised to make decisions

**Scope Changes:** Any changes to the agreed project scope will be documented in a change order and may affect timelines and costs.`,
  },
  {
    id: 'warranty',
    title: '6. Warranty & Support',
    content: `**Bug Warranty:** We provide a 30-day bug-fix warranty from final delivery. During this period, we will fix defects that prevent the software from functioning as specified in the agreed requirements — at no additional cost.

**Exclusions:** The warranty does not cover:
• Issues caused by modifications made by you or third parties after delivery
• Problems arising from third-party services or integrations outside our control
• Feature requests or enhancements beyond the original scope

**Ongoing Support:** Post-warranty maintenance and support packages are available under separate agreements.

**No Guarantees on Third-Party Services:** We cannot guarantee the uptime or performance of third-party services (payment gateways, cloud hosting, APIs) integrated into your solution.`,
  },
  {
    id: 'liability',
    title: '7. Limitation of Liability',
    content: `**To the maximum extent permitted by applicable law:**

• Our total liability for any claim arising from our services is limited to the amount paid by you for the specific service in question during the 3 months preceding the claim.

• We are not liable for indirect, incidental, consequential, or punitive damages including loss of profits, data, business, or goodwill.

• We are not liable for losses arising from: data loss due to hosting or server failures, third-party service outages, security breaches caused by factors outside our control, or your failure to maintain software and security updates.

**Indemnification:** You agree to indemnify and hold Vibhraad Technology harmless from any claims, damages, or expenses arising from your use of our services, your violation of these terms, or content you provide that infringes third-party rights.`,
  },
  {
    id: 'termination',
    title: '8. Termination',
    content: `**By Client:** You may terminate a project agreement by providing 14 days written notice. You will be invoiced for all work completed up to the termination date.

**By Vibhraad Technology:** We reserve the right to terminate services if:
• Payment obligations are not met after 30 days past due
• You engage in conduct that is abusive, threatening, or unlawful
• Continuing the project would require us to violate applicable laws

**Upon Termination:** We will deliver all completed work and associated files to you upon receipt of payment for work completed. Proprietary frameworks and tools remain our property.

**Data Deletion:** Upon request, we will delete your data from our systems within 30 days of project termination, subject to legal retention requirements.`,
  },
  {
    id: 'conduct',
    title: '9. Acceptable Use',
    content: `You agree not to use our services to develop, promote, or operate:

• Illegal products or services
• Platforms facilitating fraud, scams, or deceptive practices
• Systems designed to harass, threaten, or harm individuals
• Content that infringes copyright, trademarks, or other intellectual property rights
• Malware, spyware, or any software intended to harm users or systems
• Platforms that violate data protection laws or exploit personal data without consent

**We reserve the right to refuse, suspend, or terminate any engagement** that we determine violates these acceptable use standards, without liability to you.`,
  },
  {
    id: 'governing',
    title: '10. Governing Law & Disputes',
    content: `These Terms of Service are governed by the laws of India, specifically applicable statutes including the Information Technology Act, 2000, and the Indian Contract Act, 1872.

**Dispute Resolution:** In the event of any dispute:
1. Both parties agree to first attempt good-faith resolution through direct negotiation within 30 days.
2. If unresolved, disputes will be submitted to mediation before proceeding to legal action.
3. Any legal proceedings shall be subject to the exclusive jurisdiction of the courts in Noida / Gautam Buddha Nagar, Uttar Pradesh, India.

**Severability:** If any provision of these Terms is found unenforceable, the remaining provisions continue in full force and effect.`,
  },
];

function AccordionItem({ section, index }) {
  const [open, setOpen] = useState(index === 0);

  const formatContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.trim() === '') return <br key={i} />;
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="mb-2 text-gray-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.04 }}
      className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm mb-3"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-bold text-gray-900 text-sm">{section.title}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <ChevronDown className="w-5 h-5 text-blue-500 flex-shrink-0" />
        </motion.span>
      </button>
      {open && (
        <div className="px-6 pb-5 pt-2 bg-gray-50/50 border-t border-gray-100">
          {formatContent(section.content)}
        </div>
      )}
    </motion.div>
  );
}

export default function TermsOfService() {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-gray-950 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-indigo-700/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-600/30">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Terms of Service</h1>
            <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed mb-4">
              Please read these terms carefully before engaging our services. By working with us, you agree to these terms.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-gray-400 text-xs px-4 py-2 rounded-full">
              Last Updated: April 16, 2026 · Effective: October 2025
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 max-w-4xl mx-auto px-5">
        {/* Notice box */}
        <div className="flex gap-3 bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-amber-800 text-sm leading-relaxed">
            <strong>Important:</strong> These terms govern all engagements with Vibhraad Technology. Individual projects are also governed by project-specific contracts, which take precedence over these general terms in case of conflict.
          </p>
        </div>

        {SECTIONS.map((section, i) => (
          <AccordionItem key={section.id} section={section} index={i} />
        ))}

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 bg-gray-950 rounded-3xl p-8 text-white"
        >
          <h2 className="text-xl font-bold mb-2">Questions About These Terms?</h2>
          <p className="text-gray-400 text-sm mb-5">Our team will clarify any questions about our service terms within 48 hours.</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600/20 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-indigo-400" />
              </div>
              <a href="mailto:vibhraad@gmail.com" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium transition-colors">vibhraad@gmail.com</a>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600/20 rounded-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-indigo-400" />
              </div>
              <span className="text-gray-400 text-sm">Vibhraad Technology Pvt Ltd, F-375, Sector-36, Noida, UP 201301</span>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
